import { useState } from "react";

interface FormData {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  company: string;
  address: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  saveInfo: boolean;
  cardName: string;
  cardNumber: string;
  expiry: string;
  cvv: string;
}

export type { FormData };

interface CheckoutFormProps {
  shippingCost: number;
  onShippingChange: (cost: number) => void;
  onSubmit: (formData: FormData) => void;
}

const SHIPPING_OPTIONS = [
  { id: "standard", label: "Standard Shipping", desc: "5–7 business days", price: 0, note: "Free" },
  { id: "express", label: "Express Shipping", desc: "3–5 business days", price: 12, note: "$12.00" },
  { id: "overnight", label: "Overnight Delivery", desc: "Next business day", price: 28, note: "$28.00" },
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

function formatCardNumber(val: string) {
  return val.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}

function formatExpiry(val: string) {
  const digits = val.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

function getCardType(num: string): string {
  const n = num.replace(/\s/g, "");
  if (/^4/.test(n)) return "ri-visa-line";
  if (/^5[1-5]/.test(n)) return "ri-mastercard-line";
  if (/^3[47]/.test(n)) return "ri-bank-card-line";
  return "ri-bank-card-line";
}

export default function CheckoutForm({ onShippingChange, onSubmit }: CheckoutFormProps) {
  const [selectedShipping, setSelectedShipping] = useState("standard");
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [form, setForm] = useState<FormData>({
    email: "", phone: "", firstName: "", lastName: "",
    company: "", address: "", apt: "", city: "",
    state: "", zip: "", country: "United States",
    saveInfo: false,
    cardName: "", cardNumber: "", expiry: "", cvv: "",
  });

  const set = (key: keyof FormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleShipping = (id: string, price: number) => {
    setSelectedShipping(id);
    onShippingChange(price);
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!form.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = "Invalid email";
    if (!form.firstName) newErrors.firstName = "First name is required";
    if (!form.lastName) newErrors.lastName = "Last name is required";
    if (!form.address) newErrors.address = "Address is required";
    if (!form.city) newErrors.city = "City is required";
    if (!form.state) newErrors.state = "State is required";
    if (!form.zip) newErrors.zip = "ZIP code is required";
    if (!form.cardName) newErrors.cardName = "Name is required";
    if (!form.cardNumber || form.cardNumber.replace(/\s/g, "").length < 16) newErrors.cardNumber = "Valid card number required";
    if (!form.expiry || form.expiry.length < 5) newErrors.expiry = "Valid expiry required";
    if (!form.cvv || form.cvv.length < 3) newErrors.cvv = "Valid CVV required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(form);
    }
  };

  const inputClass = (field: keyof FormData) =>
    `w-full border px-3 py-3 text-sm text-midnight placeholder-midnight/30 focus:outline-none transition-colors bg-white ${
      errors[field]
        ? "border-red-300 focus:border-red-400"
        : "border-pearl focus:border-midnight/40"
    }`;

  const labelClass = "block text-[10px] tracking-widest uppercase text-midnight/50 mb-1.5";

  return (
    <form onSubmit={handleSubmit} className="space-y-8" style={{ fontFamily: "'Jost', sans-serif" }}>

      {/* ── Section 1: Contact ── */}
      <div>
        <h2 className="text-lg font-light text-midnight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Contact Information
        </h2>
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Email address *</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="your@email.com"
              className={inputClass("email")}
            />
            {errors.email && <p className="text-[10px] text-red-400 mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className={labelClass}>Phone number</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+1 (555) 000-0000"
              className={inputClass("phone")}
            />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.saveInfo}
              onChange={(e) => set("saveInfo", e.target.checked)}
              className="w-4 h-4 accent-midnight cursor-pointer"
            />
            <span className="text-xs text-midnight/50">Email me with news and exclusive offers</span>
          </label>
        </div>
      </div>

      {/* ── Section 2: Shipping Address ── */}
      <div>
        <h2 className="text-lg font-light text-midnight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Shipping Address
        </h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>First name *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => set("firstName", e.target.value)}
                placeholder="First name"
                className={inputClass("firstName")}
              />
              {errors.firstName && <p className="text-[10px] text-red-400 mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className={labelClass}>Last name *</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => set("lastName", e.target.value)}
                placeholder="Last name"
                className={inputClass("lastName")}
              />
              {errors.lastName && <p className="text-[10px] text-red-400 mt-1">{errors.lastName}</p>}
            </div>
          </div>
          <div>
            <label className={labelClass}>Company (optional)</label>
            <input
              type="text"
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              placeholder="Company"
              className={inputClass("company")}
            />
          </div>
          <div>
            <label className={labelClass}>Address *</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="Street address"
              className={inputClass("address")}
            />
            {errors.address && <p className="text-[10px] text-red-400 mt-1">{errors.address}</p>}
          </div>
          <div>
            <label className={labelClass}>Apartment, suite, etc. (optional)</label>
            <input
              type="text"
              value={form.apt}
              onChange={(e) => set("apt", e.target.value)}
              placeholder="Apt, suite, unit, etc."
              className={inputClass("apt")}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className={labelClass}>City *</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="City"
                className={inputClass("city")}
              />
              {errors.city && <p className="text-[10px] text-red-400 mt-1">{errors.city}</p>}
            </div>
            <div>
              <label className={labelClass}>State *</label>
              <select
                value={form.state}
                onChange={(e) => set("state", e.target.value)}
                className={`${inputClass("state")} appearance-none cursor-pointer`}
              >
                <option value="">State</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {errors.state && <p className="text-[10px] text-red-400 mt-1">{errors.state}</p>}
            </div>
            <div>
              <label className={labelClass}>ZIP code *</label>
              <input
                type="text"
                value={form.zip}
                onChange={(e) => set("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="00000"
                className={inputClass("zip")}
              />
              {errors.zip && <p className="text-[10px] text-red-400 mt-1">{errors.zip}</p>}
            </div>
          </div>
          <div>
            <label className={labelClass}>Country</label>
            <select
              value={form.country}
              onChange={(e) => set("country", e.target.value)}
              className={`${inputClass("country")} appearance-none cursor-pointer`}
            >
              <option>United States</option>
              <option>Canada</option>
              <option>United Kingdom</option>
              <option>Australia</option>
              <option>France</option>
              <option>Germany</option>
              <option>Japan</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Section 3: Shipping Method ── */}
      <div>
        <h2 className="text-lg font-light text-midnight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Shipping Method
        </h2>
        <div className="space-y-2.5">
          {SHIPPING_OPTIONS.map((opt) => (
            <label
              key={opt.id}
              className={`flex items-center justify-between border px-4 py-3.5 cursor-pointer transition-all duration-200 rounded ${
                selectedShipping === opt.id
                  ? "border-midnight bg-midnight/4"
                  : "border-pearl hover:border-midnight/30 bg-white"
              }`}
            >
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="shipping"
                  value={opt.id}
                  checked={selectedShipping === opt.id}
                  onChange={() => handleShipping(opt.id, opt.price)}
                  className="accent-midnight cursor-pointer"
                />
                <div>
                  <p className="text-sm font-medium text-midnight">{opt.label}</p>
                  <p className="text-[10px] text-midnight/40 mt-0.5">{opt.desc}</p>
                </div>
              </div>
              <span className={`text-sm font-medium whitespace-nowrap ${opt.price === 0 ? "text-green-600" : "text-midnight"}`}>
                {opt.note}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* ── Section 4: Payment ── */}
      <div>
        <h2 className="text-lg font-light text-midnight mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Payment
        </h2>

        {/* Secure notice */}
        <div className="flex items-center gap-2 mb-4 px-3 py-2.5 bg-[#F5F2ED] rounded border border-pearl/40">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-lock-fill text-xs text-midnight/40" />
          </div>
          <p className="text-[10px] text-midnight/40 tracking-wide">All transactions are secure and encrypted.</p>
        </div>

        <div className="border border-pearl rounded overflow-hidden bg-white">
          <div className="px-4 pt-4 pb-3 border-b border-pearl/50">
            {/* Card Number */}
            <label className={labelClass}>Card number</label>
            <div className="relative">
              <input
                type="text"
                value={form.cardNumber}
                onChange={(e) => set("cardNumber", formatCardNumber(e.target.value))}
                placeholder="1234 5678 9012 3456"
                className={`w-full border-0 focus:outline-none text-sm text-midnight placeholder-midnight/25 pr-10 py-1 bg-transparent`}
              />
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                <i className={`${getCardType(form.cardNumber)} text-base text-midnight/30`} />
              </div>
            </div>
            {errors.cardNumber && <p className="text-[10px] text-red-400 mt-1">{errors.cardNumber}</p>}
          </div>
          <div className="grid grid-cols-2 divide-x divide-pearl/50">
            <div className="px-4 py-3">
              <label className={labelClass}>Expiration date</label>
              <input
                type="text"
                value={form.expiry}
                onChange={(e) => set("expiry", formatExpiry(e.target.value))}
                placeholder="MM/YY"
                className="w-full border-0 focus:outline-none text-sm text-midnight placeholder-midnight/25 bg-transparent"
              />
              {errors.expiry && <p className="text-[10px] text-red-400 mt-1">{errors.expiry}</p>}
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center gap-1 mb-1.5">
                <label className={`${labelClass} mb-0`}>CVV</label>
                <div className="relative group w-4 h-4 flex items-center justify-center cursor-help">
                  <i className="ri-question-line text-[10px] text-midnight/30" />
                  <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-midnight text-moonlight text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    3 or 4 digits on back of card
                  </div>
                </div>
              </div>
              <input
                type="text"
                value={form.cvv}
                onChange={(e) => set("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="CVV"
                className="w-full border-0 focus:outline-none text-sm text-midnight placeholder-midnight/25 bg-transparent"
              />
              {errors.cvv && <p className="text-[10px] text-red-400 mt-1">{errors.cvv}</p>}
            </div>
          </div>
          <div className="px-4 py-3 border-t border-pearl/50">
            <label className={labelClass}>Name on card</label>
            <input
              type="text"
              value={form.cardName}
              onChange={(e) => set("cardName", e.target.value)}
              placeholder="Full name as on card"
              className="w-full border-0 focus:outline-none text-sm text-midnight placeholder-midnight/25 bg-transparent"
            />
            {errors.cardName && <p className="text-[10px] text-red-400 mt-1">{errors.cardName}</p>}
          </div>
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        className="w-full py-4 bg-midnight text-moonlight text-sm tracking-widest uppercase font-medium hover:bg-midnight/90 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 rounded"
      >
        <i className="ri-lock-line text-xs" />
        Place Order
      </button>

      <p className="text-[10px] text-midnight/35 text-center leading-relaxed">
        By placing your order, you agree to our{" "}
        <a href="/pages/privacy" className="underline hover:text-midnight transition-colors">Privacy Policy</a>
        {" "}and{" "}
        <a href="/pages/returns" className="underline hover:text-midnight transition-colors">Terms of Service</a>.
      </p>
    </form>
  );
}
